import type { BackOfficeMessageKey } from '@eklevo/shared/i18n/backoffice'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useNavigate, useSearch } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'
import { Button } from '../../components/ui/button'
import { Card } from '../../components/ui/card'
import { useFragmentToken } from '../../lib/fragment-token'
import { Problem } from '../components/chrome'
import { useI18n } from '../lib/i18n'
import { useStaffState } from '../lib/session'
import { END_REASONS, type EndReason } from '../lib/session-end'
import { STAFF_STATE_KEY, useTRPC } from '../lib/trpc'

/*
 * The staff's sign-in, first step (ADR 0008 §1): the email link, with the people's rules. The
 * token travels in the URL's fragment and only this page's POST consumes it; the answer to a
 * request is always the same. Then the passkey (/clave).
 */

export interface SignInSearch {
  /** Why the last session ended, to say so (lib/session-end.ts). */
  motivo?: EndReason | undefined
}

export function validateSignInSearch(search: Record<string, unknown>): SignInSearch {
  const { motivo } = search
  return {
    motivo: END_REASONS.find((r) => r === motivo),
  }
}

const ENDED: Record<EndReason, BackOfficeMessageKey> = {
  salida: 'signIn.ended.signedOut',
  inactividad: 'signIn.ended.idle',
  caducada: 'signIn.ended.expired',
  cerrada: 'signIn.ended.closed',
}

export function SignInPage() {
  const search = useSearch({ from: '/_public/entrar' })
  const token = useFragmentToken()
  const state = useStaffState()
  if (!token && state.data?.status === 'signed-in') return <Navigate to="/" replace />
  return token ? <ConfirmLink token={token} /> : <RequestLink ended={search.motivo} />
}

function RequestLink({ ended }: { ended: EndReason | undefined }) {
  const { t, locale } = useI18n()
  const trpc = useTRPC()
  const [email, setEmail] = useState('')
  const [invalid, setInvalid] = useState(false)
  const request = useMutation(trpc.auth.requestLink.mutationOptions())

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const value = email.trim()
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setInvalid(true)
      return
    }
    setInvalid(false)
    // The email goes in the language of the page.
    request.mutate({ email: value, locale })
  }

  return (
    <Card>
      <h1 className="text-2xl">{t('signIn.title')}</h1>
      {ended && !request.isSuccess && (
        <p role="status" className="mt-4 rounded-lg border border-line bg-page px-3 py-2 text-sm">
          {t(ENDED[ended])}
        </p>
      )}
      {request.isSuccess ? (
        <p role="status" className="mt-6 text-ink-muted">
          {t('signIn.sent')}
        </p>
      ) : (
        <>
          <p className="mt-3 text-sm text-ink-muted">{t('signIn.intro')}</p>
          <form className="mt-6 space-y-4" onSubmit={submit} noValidate>
            <div>
              <label htmlFor="email" className="block text-sm font-medium">
                {t('signIn.emailLabel')}
              </label>
              <input
                id="email"
                type="email"
                autoComplete="username"
                inputMode="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={invalid}
                aria-describedby={invalid ? 'email-error' : undefined}
                className="mt-1.5 w-full rounded-lg border border-line bg-surface px-3 py-2.5"
              />
              {invalid && (
                <p id="email-error" className="mt-1.5 text-sm text-danger">
                  {t('signIn.invalidEmail')}
                </p>
              )}
            </div>
            <Problem>{request.isError ? t('common.error.generic') : null}</Problem>
            <Button type="submit" className="w-full" disabled={request.isPending}>
              {request.isPending ? t('signIn.sending') : t('signIn.submit')}
            </Button>
          </form>
        </>
      )}
    </Card>
  )
}

function ConfirmLink({ token }: { token: string }) {
  const { t } = useI18n()
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const confirm = useMutation(
    trpc.auth.confirmLink.mutationOptions({
      onSuccess: async (r) => {
        if (!r.ok) return
        await queryClient.invalidateQueries({ queryKey: STAFF_STATE_KEY })
        await navigate({ to: '/clave', replace: true })
      },
    }),
  )
  if (confirm.data && !confirm.data.ok) {
    return (
      <Card>
        <h1 className="text-2xl">{t('signIn.linkInvalid.title')}</h1>
        <p className="mt-3 text-ink-muted">{t('signIn.linkInvalid.body')}</p>
        <Link
          to="/entrar"
          reloadDocument
          className="mt-6 inline-flex rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-contrast hover:bg-primary-hover"
        >
          {t('signIn.linkInvalid.action')}
        </Link>
      </Card>
    )
  }
  const busy = confirm.isPending || confirm.data?.ok === true
  return (
    <Card>
      <h1 className="text-2xl">{t('signIn.title')}</h1>
      <p className="mt-3 text-ink-muted">{t('signIn.confirm.body')}</p>
      <Problem>{confirm.isError ? t('common.error.generic') : null}</Problem>
      <Button
        size="lg"
        className="mt-6 w-full"
        disabled={busy}
        onClick={() => confirm.mutate({ token })}
      >
        {busy ? t('signIn.confirm.busy') : t('signIn.confirm.submit')}
      </Button>
    </Card>
  )
}
